/*
  <product-card-v3>
  Controller for the redesigned product card (duplicated from product-card-v2,
  with the one-time / subscribe purchase logic removed — one-time only).
  Responsibilities:
    - Quantity stepper (+ / -)
    - Weight <select> -> keep [name="id"] in sync, update the visible price
    - Wishlist heart toggle
    - Post-add "Added" feedback

  Add-to-cart is submitted by the theme's <product-form> element (product-form.js),
  so cart drawer / AJAX behaviour is inherited unchanged. No selling_plan field
  exists in the form, so lines are always added as regular one-time purchases.
*/
if (!customElements.get('product-card-v3')) {
  class ProductCardV3 extends HTMLElement {
    connectedCallback() {
      this.select = this.querySelector('.pcv3__select select');
      this.idInput = this.querySelector('[data-pcv3-id]');
      this.qtyInput = this.querySelector('.pcv3__qty-input');
      this.priceAmount = this.querySelector('.pcv3__price-amount');
      this.priceWas = this.querySelector('.pcv3__price-was');
      this.atc = this.querySelector('.pcv3__atc');
      this.wish = this.querySelector('.pcv3__wish');

      this.bindQuantity();
      this.bindSelect();
      this.bindWishlist();
      this.bindAddedFeedback();
      this.enforceOneTimePurchase();
    }

    /* ---------- One-time purchase only ----------
       The card ships without selling-plan fields, but subscription apps (e.g.
       Seal Subscriptions) can inject their own into any visible add-to-cart
       form. Their UI is hidden by CSS; this removes the fields themselves so
       the line is always added as a regular one-time purchase. */
    enforceOneTimePurchase() {
      const form = this.querySelector('form');
      if (!form) return;
      const strip = () => {
        form.querySelectorAll('[name="selling_plan"]').forEach((el) => el.remove());
      };
      strip();
      form.addEventListener('submit', strip, true);
    }

    getVariantId() {
      if (this.select) return this.select.value;
      if (this.idInput) return this.idInput.value;
      return null;
    }

    /* ---------- Quantity stepper ---------- */
    bindQuantity() {
      if (!this.qtyInput) return;
      const min = parseInt(this.qtyInput.min, 10) || 1;

      this.querySelectorAll('.pcv3__qty-btn').forEach((btn) => {
        btn.addEventListener('click', () => {
          const step = btn.dataset.pcv3Step === 'down' ? -1 : 1;
          const current = parseInt(this.qtyInput.value, 10) || min;
          const next = Math.max(min, current + step);
          this.qtyInput.value = next;
          this.qtyInput.dispatchEvent(new Event('change', { bubbles: true }));
        });
      });

      this.qtyInput.addEventListener('change', () => {
        const val = parseInt(this.qtyInput.value, 10);
        if (Number.isNaN(val) || val < min) this.qtyInput.value = min;
      });
    }

    /* ---------- Variant <select> ---------- */
    bindSelect() {
      if (!this.select) return;
      this.select.addEventListener('change', () => this.syncFromSelect());
      // Align the price, hidden id and button state with the initially
      // selected option too: the browser may restore a previous (possibly
      // sold-out) selection on back-navigation.
      this.syncFromSelect();
    }

    syncFromSelect() {
      const opt = this.select.options[this.select.selectedIndex];
      if (!opt) return;

        if (this.idInput) this.idInput.value = opt.value;

        // Update the visible price.
        if (this.priceAmount && opt.dataset.price) {
          this.priceAmount.textContent = opt.dataset.price;
        }
        if (this.priceWas) {
          if (opt.dataset.compare) {
            this.priceWas.textContent = opt.dataset.compare;
            this.priceWas.hidden = false;
          } else {
            this.priceWas.hidden = true;
          }
        }

        // Reflect availability on the button. aria-disabled must be kept in
        // sync too: the card renders it alongside `disabled` for sold-out
        // variants, and both the styling and product-form.js's submit guard
        // read it — leaving it stale keeps the button dead after switching
        // to an in-stock variant.
        if (this.atc) {
          const unavailable = opt.dataset.available === 'false';
          this.atc.toggleAttribute('disabled', unavailable);
          if (unavailable) {
            this.atc.setAttribute('aria-disabled', 'true');
          } else {
            this.atc.removeAttribute('aria-disabled');
          }
          const label = this.atc.querySelector('.pcv3__atc-label');
          if (label) {
            label.textContent = unavailable
              ? (this.dataset.soldOutText || 'Sold out')
              : (this.dataset.addText || 'Add to Cart');
          }
        }
    }

    /* ---------- Wishlist heart (persisted via window.cibFavorites) ---------- */
    bindWishlist() {
      if (!this.wish) return;

      // Restore the saved state whenever a card is rendered/connected.
      this.syncWishFromStore();

      // Keep every instance of this product in sync (other cards, other
      // sections, other tabs). productId null = bulk change, re-check own state.
      this._onFavoritesChange = (e) => {
        const d = e.detail || {};
        if (d.productId === null || String(d.productId) === String(this.dataset.productId)) {
          this.syncWishFromStore();
        }
      };
      document.addEventListener('cib:favorites:change', this._onFavoritesChange);

      this.wish.addEventListener('click', (e) => {
        e.preventDefault();
        let active;
        if (window.cibFavorites && this.dataset.productId) {
          active = window.cibFavorites.toggle(this.dataset.productId);
          this.setWishState(active); // own card updates instantly; peers via event
        } else {
          // Store unavailable — keep the original per-card visual toggle.
          active = this.wish.classList.toggle('is-active');
          this.wish.setAttribute('aria-pressed', active ? 'true' : 'false');
        }
        this.dispatchEvent(
          new CustomEvent('pcv3:wishlist', {
            bubbles: true,
            detail: { productId: this.dataset.productId, active }
          })
        );
      });
    }

    setWishState(active) {
      if (!this.wish) return;
      this.wish.classList.toggle('is-active', !!active);
      this.wish.setAttribute('aria-pressed', active ? 'true' : 'false');
    }

    syncWishFromStore() {
      if (window.cibFavorites && this.dataset.productId) {
        this.setWishState(window.cibFavorites.has(this.dataset.productId));
      }
    }

    disconnectedCallback() {
      if (this._onFavoritesChange) {
        document.removeEventListener('cib:favorites:change', this._onFavoritesChange);
        this._onFavoritesChange = null;
      }
    }

    /* ---------- Post-add feedback ---------- */
    bindAddedFeedback() {
      this.addEventListener('on:cart:add', () => this.showAdded());
      this.addEventListener('on:line-item:change', () => this.showAdded());
    }

    showAdded() {
      this.classList.add('is-added');
      const label = this.atc && this.atc.querySelector('.pcv3__atc-label');
      if (label) {
        const original = this.dataset.addText || label.textContent;
        label.textContent = this.dataset.addedText || 'Added ✓';
        window.setTimeout(() => {
          label.textContent = original;
          this.classList.remove('is-added');
        }, 1800);
      }
    }
  }

  customElements.define('product-card-v3', ProductCardV3);
}
